<script setup lang="ts">
  import type { MenuInfo } from "ant-design-vue/es/menu/src/interface";
  import { computed, onMounted, ref } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import AppDownload from "~/components/AppDownload.client.vue";
  import InfoMenu from "~/components/InfoMenu.vue";
  import HeaderBanner from "~/components/deli-module/HeaderBanner.vue";
  import { useUserInfo } from "~/composables/useUserInfo";
  import { ACCESS_TOKEN } from "~/enum/cache/cookie";
  import { DATA_DOWNLOAD_URL, LOGIN_URL, OPEN_URL, REGISTER_URL } from "~/env";
  import { useMainStore } from "~/stores";
  import Common from "~/utils/common";
  import Cookie from "~/utils/cookie";
  import Logger from "~/utils/logger";

  const store = useMainStore();
  const { getUserInfo } = useUserInfo();

  const route = useRoute();
  const router = useRouter();

  const headerItemTabs = [
    {
      name: "home",
      value: "首页",
      path: "/",
    },
    {
      name: "product",
      value: "产品中心",
      path: "",
      child: [
        {
          name: "ai",
          value: "小理AI",
          path: "/aigc",
        },
        {
          name: "legalSearchProfessional",
          value: "得理法搜专业版",
          path: "/search/professional",
        },
        {
          name: "legalSearchEnterprise",
          value: "得理法搜企业版",
          path: "/search/enterprise",
        },
        {
          name: "paralegal",
          value: "得理律助",
          path: "/paralegal",
        },
      ],
    },
    {
      name: "news",
      value: "资讯动态",
      path: "/news",
    },
    {
      name: "help",
      value: "帮助中心",
      path: "/help/result",
    },
    {
      name: "open",
      value: "生态合作",
      path: OPEN_URL,
    },
    {
      name: "lawyerEnrollment",
      value: "律师入驻",
      path: "/settlement",
    },
    {
      name: "recruitment",
      value: "人才招聘",
      path: "/recruitment",
    },
    {
      name: "about",
      value: "关于我们",
      path: "/about",
    },
  ];

  const routePath = computed<number | null>(() => {
    const routePathNow = route.path;
    if (routePathNow === "/settlement") {
      return 1;
    } else if (
      routePathNow.indexOf("/help/result") !== -1 ||
      routePathNow.indexOf("/news/literacy") !== -1 ||
      routePathNow.indexOf("/news/industry") !== -1 ||
      routePathNow.indexOf("/news/company") !== -1
    ) {
      return 2;
    } else if (routePathNow === "/question") {
      return 3;
    } else {
      return null;
    }
  });

  const isLogin = computed<boolean | null>(() => {
    if (process.client) {
      return store.login;
    }
    return false;
  });

  const productRoute = computed<boolean>(() => {
    if (process.client) {
      return ["/aigc", "/paralegal", "/search/professional", "/search/enterprise"].includes(route.path);
    }
    return false;
  });

  const getPopupContainer = (): HTMLElement => {
    return document.querySelector(".header-menu") || document.body;
  };

  const onToOpen = (name: string, url: string) => {
    if (name === "open") {
      Logger.event("官网访问_开放平台");
      return Common.openWin(url);
    }
  };

  const onLogin = () => {
    location.replace(`${LOGIN_URL}&redirect=${encodeURIComponent(location.href)}`);
  };

  const onRegister = () => {
    location.replace(`${REGISTER_URL}&redirect=${encodeURIComponent(location.href)}`);
  };

  // 展开菜单
  const isShowMenu = ref<boolean>(false);
  const expandMenu = () => {
    isShowMenu.value = true;
  };
  const closeMenu = () => {
    isShowMenu.value = false;
  };

  const handleClick = (info: MenuInfo) => {
    isShowMenu.value = false;
    const key = String(info.key); // 将 key 转换为字符串
    if (key === "17") {
      onLogin();
      return;
    }
    // 退出登录
    if (key === "18") {
      store.logout().then(() => {
        location.reload();
      });
    }
  };
  //产品中心埋点
  const onTrigerProduct = (item: Record<string, any>) => {
    switch (item.name) {
      case "ai":
        Logger.event("首页菜单_小理AI");
        break;
      case "paralegal":
        Logger.event("首页菜单_得理律助");
        break;
      case "legalSearch":
        Logger.event("首页菜单_得理法搜");
        break;
      default:
        break;
    }
    router.push(item.path);
  };

  onMounted(() => {
    Cookie.get(ACCESS_TOKEN) && getUserInfo();
  });
</script>

<template>
  <div
    class="page-header"
    :class="[
      { 'page-header-absolute': routePath === 1 },
      { 'page-header-color': routePath === 2 },
      { 'page-header-black': routePath === 3 },
    ]">
    <client-only>
      <HeaderBanner class="tw-hidden md:tw-block" url-type="1" />
    </client-only>
    <div class="header-wrap">
      <div class="header-content tw-flex tw-items-center tw-justify-between md:tw-flex">
        <div class="logo-default tw-flex tw-h-full tw-items-center">
          <nuxt-link class="logo-link tw-h-[38px] tw-w-[67px]" to="/">
            <img v-show="!routePath" class="tw-size-full" src="~/assets/images/header/nav.png" />
            <img
              v-show="routePath === 1 || routePath === 2 || routePath === 3"
              class="tw-size-full"
              src="~/assets/images/logo.svg" />
          </nuxt-link>
        </div>
        <div class="tw-hidden tw-h-full md:tw-block">
          <div class="header-menu tw-flex tw-h-full tw-items-center tw-text-[16px] tw-text-white">
            <div class="header-menu-container tw-flex tw-h-full tw-items-center md:tw-flex-wrap lg:tw-flex-nowrap">
              <div
                v-for="item in headerItemTabs"
                :key="item.name"
                class="header-menu-item tw-mr-[6px] sm:tw-mr-[10px] md:tw-mr-[16px] lg:tw-mr-[30px] lg:first:tw-mr-[70px]">
                <div v-if="item.name === 'open'">
                  <nuxt-link @click="onToOpen(item.name, item.path)" :aria-label="item.value">
                    <span>{{ item.value }}</span>
                  </nuxt-link>
                </div>
                <div v-else>
                  <div v-if="item.name === 'product'" class="product-center tw-relative tw-cursor-pointer">
                    <a-tooltip
                      overlay-class-name="link-header-wrapper tw-w-[228px]"
                      placement="bottom"
                      :get-popup-container="getPopupContainer">
                      <template #title>
                        <div class="product-link-content tw-cursor-pointer">
                          <div
                            v-for="subItem in item.child"
                            :key="subItem.name"
                            class="tw-block tw-h-[44px] tw-text-center tw-leading-[44px] hover:tw-bg-[#4285F4]/[.25]"
                            @click="onTrigerProduct(subItem)">
                            {{ subItem.value }}
                          </div>
                        </div>
                      </template>
                      {{ item.value }}
                    </a-tooltip>
                    <div v-show="productRoute" class="target-route"></div>
                  </div>
                  <div v-else-if="item.name === 'help'" @click="Logger.event('官网访问_帮助中心')">
                    <nuxt-link :to="item.path" :aria-label="item.value">{{ item.value }}</nuxt-link>
                  </div>
                  <nuxt-link v-else :to="item.path" :aria-label="item.value">
                    <span>{{ item.value }}</span>
                  </nuxt-link>
                </div>
              </div>
            </div>
            <AppDownload class="app-download-container tw-ml-[10px] lg:tw-ml-[20px]">
              <div
                class="app-download tw-flex tw-cursor-pointer tw-items-center tw-align-middle"
                @click="Logger.event('APP下载_点击')">
                <div class="app-download-phone tw-mr-[6px] tw-h-[20px] tw-w-[20px]">
                  <img
                    v-show="!routePath"
                    class="tw-size-full tw-align-top"
                    src="~/assets/images/header/app-download.svg" />
                  <img
                    v-show="routePath === 1 || routePath === 2 || routePath === 3"
                    class="tw-size-full tw-align-top"
                    src="~/assets/images/header/app-download-black.svg" />
                </div>
                <div>APP下载</div>
              </div>
            </AppDownload>
            <div class="login-area tw-ml-[20px] tw-h-[42px] sm:tw-ml-[0px] md:tw-ml-[6px] lg:tw-ml-[60px]">
              <InfoMenu v-if="isLogin" />
              <div v-else class="login-wrap tw-flex tw-h-full tw-items-center">
                <div
                  class="login sm:tw-px-[10px] md:tw-mr-[6px] md:tw-min-w-[60px] md:tw-px-[16px] lg:tw-mr-[12px] lg:tw-px-[22px] xl:tw-mr-[20px]
                    xl:tw-px-[42px]"
                  @click="onLogin()">
                  <span class="text-login">登录</span>
                </div>
                <div
                  class="register sm:tw-px-[4px] md:tw-min-w-[70px] md:tw-px-[6px] lg:tw-px-[12px] xl:tw-px-[26px]"
                  @click="onRegister()">
                  <span class="text-login">免费注册</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="tab-menu tw-block tw-h-[24px] tw-w-[24px] tw-cursor-pointer md:tw-hidden" @click="expandMenu">
          <img v-show="!routePath" class="tw-size-full" src="~/assets/images/header/more-item.png" />
          <img
            v-show="routePath === 1 || routePath === 2 || routePath === 3"
            class="tw-size-full"
            src="~/assets/images/header/more-item-black.png" />
        </div>
      </div>
    </div>
    <a-modal
      class="expand-menu-container"
      :open="isShowMenu"
      wrap-class-name="expand-menu-content"
      dialog-class="expand-menu-dialog"
      width="100%"
      :mask="false"
      :mask-closable="false"
      :footer="null"
      :destroy-on-close="true"
      @cancel="closeMenu">
      <template #title>
        <div class="tw-h-[30px] tw-w-[52px]"><img class="tw-size-full" src="~/assets/images/logo.svg" /></div>
      </template>
      <template #closeIcon>
        <img class="tw-h-[24px] tw-w-[24px] tw-align-text-top" src="~/assets/images/header/close.png" />
      </template>
      <div class="menu-items-content">
        <a-menu :default-selected-keys="['0']" :default-open-keys="['2']" mode="inline" @click="handleClick">
          <a-menu-item key="1">
            <nuxt-link to="/">首页</nuxt-link>
          </a-menu-item>
          <a-sub-menu key="2">
            <template #title>
              <span>产品中心</span>
            </template>
            <a-menu-item key="3">
              <nuxt-link to="/ai">小理AI</nuxt-link>
            </a-menu-item>
            <a-menu-item key="4">
              <nuxt-link to="/paralegal"> 得理律助</nuxt-link>
            </a-menu-item>
            <a-menu-item key="5">
              <nuxt-link to="/search/professional"> 得理法搜专业版</nuxt-link>
            </a-menu-item>
            <a-menu-item key="20">
              <nuxt-link to="/search/enterprise"> 得理法搜企业版</nuxt-link>
            </a-menu-item>
          </a-sub-menu>
          <a-sub-menu key="6">
            <template #title>
              <span>资讯动态</span>
            </template>
            <a-menu-item key="7">
              <nuxt-link to="/news/company"> 公司动态</nuxt-link>
            </a-menu-item>
            <a-menu-item key="8">
              <nuxt-link to="/news"> 行业资讯</nuxt-link>
            </a-menu-item>
            <a-menu-item key="9">
              <nuxt-link to="/news/laws"> 法律知识</nuxt-link>
            </a-menu-item>
          </a-sub-menu>
          <a-sub-menu key="10">
            <template #title>
              <span>帮助中心</span>
            </template>
            <a-menu-item key="11">
              <nuxt-link to="/help/result#001"> 概要</nuxt-link>
            </a-menu-item>
            <a-menu-item key="12">
              <nuxt-link to="/help/result#002"> 小理AI</nuxt-link>
            </a-menu-item>
            <a-menu-item key="13">
              <nuxt-link to="/help/result#005"> 得理律助</nuxt-link>
            </a-menu-item>
            <a-menu-item key="14">
              <nuxt-link to="/help/result#004"> 得理法搜</nuxt-link>
            </a-menu-item>
          </a-sub-menu>
          <a-menu-item key="15">
            <a :href="OPEN_URL" target="_blank">生态合作</a>
          </a-menu-item>
          <a-menu-item key="16">
            <nuxt-link to="/settlement"> 律师入驻</nuxt-link>
          </a-menu-item>
          <a-menu-item key="20">
            <nuxt-link to="/about"> 关于我们</nuxt-link>
          </a-menu-item>
          <a-menu-item v-if="!isLogin" key="17">
            <span>登录</span>
          </a-menu-item>
          <a-menu-item v-if="isLogin" key="18">
            <span>退出登录</span>
          </a-menu-item>
          <a-menu-item key="19">
            <a :href="DATA_DOWNLOAD_URL" target="_blank">APP下载</a>
          </a-menu-item>
        </a-menu>
      </div>
    </a-modal>
  </div>
</template>

<style lang="less" scoped>
  .page-header {
    width: 100%;
    margin-bottom: -72px;

    &.page-header-color {
      margin-bottom: 0;

      .header-menu {
        color: #000;

        .header-menu-item {
          .nuxt-link-exact-active,
          &:not(:first-child) .nuxt-link-active {
            &::after {
              background: #000 !important;
            }
          }
        }
      }
    }

    &.page-header-absolute {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 99;
      margin-bottom: 0;

      .header-menu {
        color: #000;

        .header-menu-item {
          .nuxt-link-exact-active,
          &:not(:first-child) .nuxt-link-active {
            &::after {
              background: #000 !important;
            }
          }
        }
      }
    }

    &.page-header-black {
      .header-menu {
        color: #000;
      }
    }

    .header-wrap {
      height: 72px;
      padding: 0 32px 0 40px;

      .header-content {
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        margin-right: auto;
        margin-left: auto;

        .header-menu {
          .header-menu-item {
            &:first-child {
              a {
                padding-right: 4px;
                padding-left: 4px;
              }
            }

            a {
              position: relative;
              display: inline-block;
            }

            .nuxt-link-exact-active,
            &:not(:first-child) .nuxt-link-active {
              &::after {
                position: absolute;
                bottom: -10px;
                left: 50%;
                display: block;
                width: 36px;
                height: 2px;
                pointer-events: none;
                content: "";
                background: #fff;
                border-radius: 1px;
                transform: translateX(-50%);
              }
            }

            .product-center {
              .target-route {
                position: absolute;
                bottom: -10px;
                left: 50%;
                display: block;
                width: 36px;
                height: 2px;
                pointer-events: none;
                content: "";
                background: #fff;
                border-radius: 1px;
                transform: translateX(-50%);
              }
            }
          }

          :deep(.link-header-wrapper) {
            .ant-tooltip-inner {
              padding: 8px 0;
            }
          }

          .login-area {
            .login-wrap {
              .login {
                display: flex;
                align-items: center;
                height: 100%;
                cursor: pointer;
                background: #fff;
                border-radius: 4px;

                span {
                  // font-weight: 600;
                  color: transparent;
                  background-image: linear-gradient(360deg, #0a5cff 0%, #66f 100%);
                  background-clip: text;
                }
              }

              .register {
                display: flex;
                align-items: center;
                height: 100%;
                cursor: pointer;
                background: linear-gradient(90deg, #0a5cff 0%, #66f 100%);
                border-radius: 4px;

                span {
                  // font-weight: 600;
                  color: #fff;
                }
              }
            }

            :deep(.info-menu) {
              .ant-dropdown {
                top: 120px !important;
              }
            }
          }
        }
      }
    }
  }

  .expand-menu-container {
    :deep(.expand-menu-dialog) {
      top: 0;
      max-width: 100vw !important;
      height: 100vh !important;
      padding: 0;
      margin: 0;
      border: none;

      .ant-modal-content {
        width: 100%;
        height: 100%;
        border-radius: 0;

        .ant-modal-header {
          border-radius: 0;
        }

        .ant-modal-body {
          padding: 0;
          overflow: hidden;

          .menu-items-content {
            overflow: hidden;
          }
        }
      }
    }
  }

  @media screen and (min-width: 768px) and (max-width: 820px) {
    .header-menu-item {
      margin-right: 8px;
    }

    .text-login {
      font-size: 14px;
    }
  }
</style>
